/* assets/z__cart.js
   Clean + safe customElements.define()
   - cart-timer <cart-timer data-time="15">
   - cart-js <cart-js class="minicart-container|cart">
*/

(() => {
  "use strict";

  // ------------------------------------------------------------
  // Helpers
  // ------------------------------------------------------------
  const safeDefine = (name, ctor) => {
    if (!customElements.get(name) && !window.__BUD_DEFINED?.[name]) {
      window.__BUD_DEFINED = window.__BUD_DEFINED || {};
      customElements.define(name, ctor);
      window.__BUD_DEFINED[name] = true;
    }
  };

  const money = (cents) => {
    try {
      return theme?.Currency?.formatMoney ? theme.Currency.formatMoney(cents) : String(cents);
    } catch (e) {
      return String(cents);
    }
  };

  const ajaxURL = (url) => {
    // ton thème a déjà window.ajaxURL() -> on le réutilise
    if (window.ajaxURL) return window.ajaxURL(url);

    // fallback propre si jamais
    return fetch(url, { credentials: "same-origin" }).then((r) => r.text());
  };

  // ------------------------------------------------------------
  // cart-timer
  // ------------------------------------------------------------
  class CartTimerElement extends HTMLElement {
    connectedCallback() {
      const minutes = parseInt(this.dataset.time || "15", 10);
      const now = Date.now();

      let timerStart = localStorage.getItem("cartTimerActual");
      let timerEnd = localStorage.getItem("cartTimerEnd");

      if (!timerEnd) {
        timerStart = String(now);
        timerEnd = String(now + minutes * 60 * 1000);
        localStorage.setItem("cartTimerActual", timerStart);
        localStorage.setItem("cartTimerEnd", timerEnd);
      }

      const update = () => {
        const current = Date.now();
        localStorage.setItem("cartTimerActual", String(current));

        const end = parseInt(localStorage.getItem("cartTimerEnd") || "0", 10);
        let diff = end - current;

        if (diff < 0) {
          this.innerHTML = `<span>${Shopify?.translation?.timer_ended || "Temps écoulé"}</span>`;
          // si vraiment très négatif (ancien timer), on purge
          if (diff < -86400000) {
            localStorage.removeItem("cartTimerInitial");
            localStorage.removeItem("cartTimerEnd");
          }
          return;
        }

        const mm = Math.floor((diff % 3600000) / 60000);
        const ss = Math.floor((diff % 60000) / 1000);

        const mmStr = mm <= 9 ? `0${mm}` : String(mm);
        const ssStr = ss <= 9 ? `0${ss}` : String(ss);

        const label =
          mm > 0
            ? (Shopify?.translation?.date?.m || "min")
            : (Shopify?.translation?.date?.minute || "min");

        const dyn = this.querySelector(".dynamic");
        if (dyn) dyn.textContent = `${mmStr}:${ssStr} ${label}`;
      };

      update();
      this.__interval = window.setInterval(update, 1000);
    }

    disconnectedCallback() {
      if (this.__interval) window.clearInterval(this.__interval);
    }
  }

  safeDefine("cart-timer", CartTimerElement);

  // ------------------------------------------------------------
  // cart-js
  // ------------------------------------------------------------
  class CartJSElement extends HTMLElement {
    connectedCallback() {
      this.cartFooter = this.querySelector("[data-sticky-mobile]");
      this.keepOnPageEnabled = this.getAttribute("data-keep-on-page") === "true";

      // sticky mobile footer behaviour
      this.refreshStickyFooter();

      window.addEventListener(
        "resize",
        theme?.utils?.debounce
          ? theme.utils.debounce(300, () => this.refreshStickyFooter())
          : () => this.refreshStickyFooter()
      );

      // initialisation
      if (this.keepOnPageEnabled) this.keepOnPage();
      if (this.querySelector("shipping-calculator")) this.shippingCalculator();
      this.initListeners();

      document.addEventListener("updateCart", this.__onUpdateCart = () => this.getCart());
    }

    disconnectedCallback() {
      if (this.__onUpdateCart) document.removeEventListener("updateCart", this.__onUpdateCart);
    }

    refreshStickyFooter() {
      if (!this.cartFooter) return;

      const enabled = this.classList.contains("cart") && this.cartFooter.getAttribute("data-sticky-mobile") === "true";
      if (!enabled) return this.unsetPosition();

      if (document.documentElement.clientWidth < 779) this.setPosition();
      else this.unsetPosition();
    }

    initListeners() {
      // Quantité +/- (liens)
      this.querySelectorAll(".quantity__button").forEach((btn) => {
        btn.addEventListener("click", (e) => {
          e.preventDefault();
          e.stopPropagation();
          const href = btn.getAttribute("href");
          if (href) this.updateItem(href);
        });
      });

      // Quantité input
      this.querySelectorAll("input.quantity__input").forEach((input) => {
        input.addEventListener("change", (e) => {
          e.preventDefault();
          e.stopPropagation();
          this.updateInputItem(input);
        });
      });

      // Changement variante
      this.querySelectorAll("select.cart__selector-variant").forEach((select) => {
        select.addEventListener("change", (e) => {
          e.preventDefault();
          this.updateVariant(select.value, select.dataset.actual, select.dataset.quantity);
        });
      });
    }

    setLoading(on) {
      if (on) {
        if (Shopify?.theme_settings?.loader) this.insertAdjacentHTML("beforeend", Shopify.theme_settings.loader);
        this.classList.add("loading");
      } else {
        this.querySelectorAll(".loader").forEach((n) => n.remove());
        this.classList.remove("loading");
      }
    }

    updateCartBubblesLoading() {
      document.querySelectorAll(".cart-bubble").forEach((b) => {
        if (Shopify?.theme_settings?.loader) b.innerHTML = Shopify.theme_settings.loader;
      });
    }

    updateItem(url) {
      this.setLoading(true);
      ajaxURL(url).then(() => document.dispatchEvent(new CustomEvent("updateCart")));
    }

    updateInputItem(input) {
      this.setLoading(true);
      this.updateCartBubblesLoading();

      const qty = input.value;
      const lineId = input.dataset.lineId;
      ajaxURL(`${theme.routes.cart_change_url}?quantity=${qty}&id=${encodeURIComponent(lineId)}`)
        .then(() => document.dispatchEvent(new CustomEvent("updateCart")));
    }

    updateVariant(variantId, actualKey, qtyMinusOne) {
      this.setLoading(true);
      this.updateCartBubblesLoading();

      ajaxURL(`${theme.routes.cart_change_url}?quantity=${qtyMinusOne}&id=${encodeURIComponent(actualKey)}`)
        .then(() => ajaxURL(`${theme.routes.cart_add_url}?quantity=1&id=${variantId}`))
        .then(() => document.dispatchEvent(new CustomEvent("updateCart")))
        .catch((e) => console.error("updateVariant error:", e));
    }

    getCart() {
      ajaxURL(theme.routes.cart_url).then((html) => this.renderCart(html));
    }

    renderCart(html) {
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, "text/html");

      // on met à jour les bulles
      const bubbleSource = doc.querySelector(".cart-bubble");
      document.querySelectorAll(".cart-bubble").forEach((b) => {
        const content = bubbleSource?.innerHTML || "";
        if (content && content.trim().length) {
          b.classList.remove("hide");
          b.innerHTML = content;
        } else {
          b.classList.add("hide");
        }
      });

      const isMini = this.classList.contains("minicart-container") && !this.closest(".cart-section");
      const rootSelector = isMini ? ".minicart-container" : ".cart-section";

      const srcRoot = doc.querySelector(rootSelector);
      if (!srcRoot) {
        console.warn("[cart-js] source root not found:", rootSelector);
        this.setLoading(false);
        return;
      }

      // ------------------------------------------------------------
// Robust empty-cart detection (safer than relying on .cart__empty)
// ------------------------------------------------------------
if (!isMini) {
  const srcHasItems =
    !!srcRoot.querySelector('[data-line-item]') ||
    !!srcRoot.querySelector('[data-cart-item]') ||
    !!srcRoot.querySelector('.cart-item') ||
    !!srcRoot.querySelector('.cart__item');

  const srcEmptyNode = srcRoot.querySelector('.cart__empty');

  // If no items OR empty node exists -> force reload to render correct empty state
  if (!srcHasItems || srcEmptyNode) {
    window.location.href = theme?.routes?.cart_url || '/cart';
    return;
  }
}


      // empty cart switch
      const srcEmpty = srcRoot.querySelector(".cart__empty");
      const hasEmptyNow = !!this.querySelector(".cart__empty");
      const shouldBeEmpty = !!srcEmpty;

      if (shouldBeEmpty && !hasEmptyNow) {
        // on cache les blocks non contenu
        this.querySelectorAll(':not(.cart__heading):not(.cart__cross-sell):not(.cart__content)[id^="block"]')
          .forEach((el) => el.classList.add("hide"));

        localStorage.removeItem("cartTimerInitial");
        localStorage.removeItem("cartTimerEnd");

        const srcCart = srcRoot.querySelector(".cart");
        const dstCart = this.querySelector(".cart");
        if (srcCart && dstCart) dstCart.innerHTML = srcCart.innerHTML;
      } else {
        // update content
        if (isMini) {
          const dstBlocks = this.querySelector(".minicart-blocks");
          const srcBlocks = srcRoot.querySelector(".minicart-blocks");
          if (dstBlocks && srcBlocks) dstBlocks.innerHTML = srcBlocks.innerHTML;

          const dstFooter = this.querySelector(".minicart-footer");
          const srcFooter = srcRoot.querySelector(".minicart-footer");
          if (dstFooter && srcFooter) dstFooter.innerHTML = srcFooter.innerHTML;
        } else {
          // page cart
          const dstContent = this.querySelector(".cart__content");
          const srcContent = srcRoot.querySelector(".cart__content");
          if (dstContent && srcContent) dstContent.innerHTML = srcContent.innerHTML;

          const dstFooter = this.querySelector(".cart__footer:not(.added)");
          const srcFooter = srcRoot.querySelector(".cart__footer:not(.added)");
          if (dstFooter && srcFooter) dstFooter.innerHTML = srcFooter.innerHTML;
        }

        // on ré-affiche les blocks
        this.querySelectorAll(':not(.cart__heading):not(.cart__cross-sell):not(.cart__content)[id^="block"]')
          .forEach((el) => el.classList.remove("hide"));
      }

      // rebinding
      this.initListeners();

      // lazyload images/svg si le thème a ça
      try {
        this.querySelectorAll("img, svg").forEach((el) => theme?.utils?.lazyBackgroundObserver?.observe?.(el));
      } catch (e) {}

      // shipping calculator refresh
      const ship = this.querySelector(".cart__shipping_calculator:not(.hide)");
      if (ship) {
        try {
          const srcBar = doc.querySelector(`${rootSelector} .cart__shipping-progress-bar`);
          const dstBar = ship.querySelector(".cart__shipping-progress-bar");
          if (srcBar && dstBar) {
            dstBar.dataset.width = srcBar.dataset.width;
            dstBar.dataset.left = srcBar.dataset.left;
            setTimeout(() => this.shippingCalculator(), 500);
          }
        } catch (e) {}
      }

      // si c'est un mini-cart ET que le thème utilise <mini-cart>, on laisse le thème l’ouvrir
      try {
        const miniCart = this.closest("mini-cart");
        if (miniCart?.bindEvents) miniCart.bindEvents();

        const atcAction = Shopify?.theme_settings?.atc_action;
        if (miniCart && (atcAction === "once" || atcAction === "always") && window.location.pathname !== "/cart") {
          const opener = document.querySelector(".menu-icons mini-cart");
          if (opener?.open) opener.open();
        }
      } catch (e) {}

      this.setLoading(false);
      document.dispatchEvent(new CustomEvent("cartRendered"));
    }

    setPosition() {
      if (!this.cartFooter) return;
      const cart = this.cartFooter.closest(".cart");
      if (cart) cart.classList.add("added");

      const footer = document.querySelector("footer");
      const content = this.cartFooter; // le bloc sticky
      if (footer && content) footer.style.marginBottom = `${content.offsetHeight}px`;
    }

    unsetPosition() {
      const footer = document.querySelector("footer");
      if (footer) footer.style.marginBottom = "";
      if (this.cartFooter) {
        const cart = this.cartFooter.closest(".cart");
        if (cart) cart.classList.remove("added");
      }
    }

    shippingCalculator() {
      const sc = this.querySelector("shipping-calculator");
      if (!sc) return;

      const bar = sc.querySelector(".cart__shipping-progress-bar");
      if (!bar) return;

      const steps = parseFloat(bar.dataset.step || "0");
      const totalWidth = parseFloat(bar.dataset.totalWidth || "0");
      const left = parseFloat(bar.dataset.left || "0");
      const width = parseFloat(bar.dataset.width || "0");

      const paid = sc.querySelector(".cart__shipping-title-paid");
      const free = sc.querySelector(".cart__shipping-title-free");
      const stepBars = sc.querySelectorAll(".cart__shipping-step-bar");

      // placement des steps
      stepBars.forEach((node, idx) => {
        const pct = (100 * (idx + 1)) / (steps + 1);
        node.style.left = `calc(${pct}% - 16px)`;
      });

      // affichage paid/free
      if (left > 0 && width < totalWidth) {
        paid?.classList.remove("hide");
        free?.classList.add("hide");

        setTimeout(() => {
          paid?.classList.remove("visibility-hidden");
          free?.classList.add("visibility-hidden");
        }, 100);

        const priceNode = paid?.querySelector(".cart__shipping-title-paid-price");
        if (priceNode) {
          const spanMoney = priceNode.querySelector("span.money");
          if (spanMoney) spanMoney.innerHTML = money(left);
          else priceNode.innerHTML = priceNode.textContent.replace("price", `<b>${money(left)}</b>`);
        }

        const wrap = sc.querySelector(".cart__shipping-progress-bar-container");
        wrap?.classList.remove("hide");
        setTimeout(() => {
          bar.style.width = `calc(${width}% - 16px)`;
        }, 500);
      } else {
        free?.classList.remove("hide");
        paid?.classList.add("hide");
        sc.querySelector(".cart__shipping-progress-bar-container")?.classList.add("hide");

        setTimeout(() => {
          free?.classList.remove("visibility-hidden");
          paid?.classList.add("visibility-hidden");
        }, 100);
      }
    }

    keepOnPage() {
      const originalTitle = document.title;
      const alt = Shopify?.translation?.keep_on_page || originalTitle;

      document.addEventListener("visibilitychange", () => {
        if (!document.hidden) {
          document.title = originalTitle;
        } else {
          setTimeout(() => (document.title = alt), 300);
        }
      });
    }
  }

  safeDefine("cart-js", CartJSElement);

})();
